<template>
  <div>
    <h3 class="title bold align--center margin__b--24">
      {{ t('literal.register') }}
    </h3>

    <sso-providers class="margin__b--18" />

    <template v-if="allowedAuthenticationForm">
      <form
        novalidate
        @submit.prevent="handleSubmitForm()"
      >
        <form-notification
          class="margin__b--12"
          :keys="error"
        />

        <fieldset>
          <label
            class="input__label"
            for="register_firstname"
          >
            {{ t('literal.firstname') }} *
          </label>

          <input-text
            id="register_firstname"
            class="margin__t--6"
            placeholder="Jon"
            autocomplete="given-name"
            :required="true"
            :v="v$.firstname"
            v-model="user.firstname"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <label
            class="input__label"
            for="register_lastname"
          >
            {{ t('literal.name') }} *
          </label>

          <input-text
            id="register_lastname"
            class="margin__t--6"
            placeholder="Snow"
            autocomplete="family-name"
            :required="true"
            :v="v$.lastname"
            v-model="user.lastname"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <label
            class="input__label"
            for="register_email"
          >
            {{ t('literal.email') }} *
          </label>

          <input-email
            id="register_email"
            class="margin__t--6"
            autocomplete="email"
            required
            :v="v$.email"
            v-model="user.email"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <label
            class="input__label"
            for="register_password"
          >
            {{ t('literal.new_password') }} *
          </label>

          <input-password
            id="register_password"
            class="margin__t--6"
            :required="true"
            :v="v$.password"
            v-model="user.password"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <label
            class="input__label"
            for="register_password_confirmation"
          >
            {{ t('literal.password_confirmation') }} *
          </label>

          <input-password
            id="register_password_confirmation"
            class="margin__t--6"
            :required="true"
            :v="v$.password_confirmation"
            v-model="user.password_confirmation"
          />
        </fieldset>

        <fieldset class="margin__t--24">
          <input-checkbox
            class="color--prim"
            :required="true"
            :v="v$.terms_of_use"
            v-model="user.terms_of_use"
          >
            <span v-html="t('@register.label_agree_terms_and_privacy')" />
          </input-checkbox>
        </fieldset>

        <div class="d-flex justify-content-center margin__t--36">
          <app-save-button
            type="submit"
            class="btn btn__size--medium btn__color--prim"
            :show-icon="false"
            :is-loading="authLoader"
            :is-disabled="ssoLoader"
          >
            {{ t('literal.create_your_account') }}
          </app-save-button>
        </div>
      </form>

      <hr class="separator separator__size--full color--dw margin__tb--24">

      <p class="align--center">
        {{ t('literal.have_an_account') }}

        <button
          type="button"
          class="link__color--prim link--naked bold"
          @click="emit('redirectTo', 'login')"
        >
          {{ t('literal.log_in') }}
        </button>
      </p>
    </template>
  </div>
</template>

<script setup>
import { defineAsyncComponent, reactive, computed, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useSsoStore } from '@/stores/SsoStore'
import { useAllowedFeatures } from '@/composables/app/useAllowedFeatures'
import { useRoute } from 'vue-router'
import { useI18n } from '@/vendors/i18n'
import useVuelidate from '@vuelidate/core'
import { required, minLength, email, sameAs } from '@vuelidate/validators'

import SsoProviders from '&/molecules/SsoProviders'
const InputText = defineAsyncComponent(() => import('&/atoms/InputText'))
const InputEmail = defineAsyncComponent(() => import('&/atoms/InputEmail'))
const InputPassword = defineAsyncComponent(() => import('&/atoms/InputPassword'))
const InputCheckbox = defineAsyncComponent(() => import('&/atoms/InputCheckbox'))
const AppSaveButton = defineAsyncComponent(() => import('&/molecules/AppSaveButton'))
const FormNotification = defineAsyncComponent(() => import('&/molecules/FormNotification'))

const emit = defineEmits(['authenticated', 'redirectTo'])

const { t, locale } = useI18n()
const { allowedAuthenticationForm } = useAllowedFeatures()
const { fullPath } = useRoute()
const { login, postAuthUser, $resetStore } = useAuthenticationStore()
const { loader: authLoader, error } = storeToRefs(useAuthenticationStore())
const { loader: ssoLoader } = storeToRefs(useSsoStore())

const user = reactive({
  firstname: null,
  lastname: null,
  email: null,
  password: null,
  password_confirmation: null,
  terms_of_use: false,
  language: locale,
  return_url: fullPath
})

const rules = computed(() => ({
  firstname: { required },
  lastname: { required },
  email: { required, email },
  password: { required, minLength: minLength(8) },
  password_confirmation: { sameAs: sameAs(user.password) },
  terms_of_use: { terms: sameAs(true) }
}))

const v$ = useVuelidate(rules, user)

const handleSubmitForm = () => {
  v$.value.$validate()

  if (v$.value.$invalid) return

  postAuthUser({ params: user })
    .then(() => login({ params: user })
      .then(() => emit('authenticated')))
}

onUnmounted(() => $resetStore('error'))
</script>

<style scoped lang="scss">
.title {
  font-size: rem(14px);
}
</style>
